import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { RestorePurchasesButton } from "@/features/paywall/RestorePurchasesButton";
import { useAuth } from "./AuthProvider";
import { APP_ID, getAuthPlatform } from "./nativeAuth";

/** Store-managed subscription pages (RevenueCat purchases go through them). */
function manageSubscriptionUrl(): string | null {
  switch (getAuthPlatform()) {
    case "ios":
      return "https://apps.apple.com/account/subscriptions";
    case "android":
      return `https://play.google.com/store/account/subscriptions?package=${APP_ID}`;
    default:
      return null;
  }
}

/**
 * Profile → "Compte": sign out, restore purchases (M10), manage subscription,
 * delete account (H5).
 * Account deletion is mandatory in-app on iOS (App Store 5.1.1(v)).
 */
export function AccountSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut, deleteAccount } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const subscriptionUrl = manageSubscriptionUrl();

  if (!user) return null;

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/", { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount();
      navigate("/", { replace: true });
    } catch {
      setDeleteError(t("auth.account.delete_failed"));
      setDeleting(false);
    }
  }

  return (
    <section
      aria-labelledby="account-title"
      className="mt-5 overflow-hidden border border-hair bg-background"
    >
      <header className="border-b border-hair bg-paper px-5 py-4">
        <h2
          id="account-title"
          className="font-serif text-[18px] italic tracking-[-0.005em]"
        >
          {t("auth.account.title")}
        </h2>
        <p className="mt-0.5 text-[12px] leading-snug text-ink-50">
          {t("auth.account.subtitle")}
        </p>
      </header>
      <div className="space-y-3 px-5 py-4">
        {user.email && (
          <p className="text-[13px] text-ink-70">
            {t("auth.account.signed_in_as", { email: user.email })}
          </p>
        )}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void handleSignOut()}
          disabled={signingOut || deleting}
        >
          {signingOut
            ? t("auth.account.signing_out")
            : t("auth.account.sign_out")}
        </Button>
        <RestorePurchasesButton disabled={signingOut || deleting} />
        {subscriptionUrl && (
          <a
            href={subscriptionUrl}
            target="_blank"
            rel="noreferrer"
            className="block text-center text-[13px] text-ink-70 underline underline-offset-4"
          >
            {t("auth.account.manage_subscription")}
          </a>
        )}
        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="block w-full pt-2 text-center text-[13px] text-destructive underline-offset-4 hover:underline"
          >
            {t("auth.account.delete")}
          </button>
        ) : (
          <div
            role="alertdialog"
            aria-labelledby="account-delete-body"
            className="space-y-3 border border-destructive/40 p-4"
          >
            <p id="account-delete-body" className="text-[13px] text-ink-70">
              {t("auth.account.delete_confirm_body")}
            </p>
            {deleteError && (
              <p className="text-[13px] text-destructive" role="alert">
                {deleteError}
              </p>
            )}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => {
                  setConfirming(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
              >
                {t("auth.account.delete_cancel")}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting
                  ? t("auth.account.deleting")
                  : t("auth.account.delete_confirm")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
